import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, TrendingUp, Info, AlertTriangle } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const STORAGE_KEY = 'break_even_data';

export const BreakEvenCalculator = () => {
  const [fixedCosts, setFixedCosts] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).fixedCosts : 50000;
  });
  
  const [variableCostPerUnit, setVariableCostPerUnit] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).variableCostPerUnit : 20;
  });
  
  const [sellingPrice, setSellingPrice] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).sellingPrice : 50;
  });
  
  const [expectedSales, setExpectedSales] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).expectedSales : 2000;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      fixedCosts,
      variableCostPerUnit,
      sellingPrice,
      expectedSales
    }));
  }, [fixedCosts, variableCostPerUnit, sellingPrice, expectedSales]);

  const calculateBreakEven = () => {
    const fixed = parseFloat(fixedCosts) || 0;
    const varCost = parseFloat(variableCostPerUnit) || 0;
    const price = parseFloat(sellingPrice) || 0;
    const sales = parseFloat(expectedSales) || 0;

    const contributionMargin = price - varCost;
    const contributionMarginRatio = price > 0 ? (contributionMargin / price) * 100 : 0;
    const breakEvenUnits = contributionMargin > 0 ? fixed / contributionMargin : 0;
    const breakEvenRevenue = breakEvenUnits * price;
    
    const totalRevenue = sales * price;
    const totalVariableCosts = sales * varCost;
    const totalCosts = fixed + totalVariableCosts;
    const profit = totalRevenue - totalCosts;
    const marginOfSafety = sales > breakEvenUnits ? sales - breakEvenUnits : 0;
    const marginOfSafetyPercent = sales > 0 ? (marginOfSafety / sales) * 100 : 0;

    return {
      contributionMargin,
      contributionMarginRatio,
      breakEvenUnits: Math.ceil(breakEvenUnits),
      breakEvenRevenue,
      profit,
      marginOfSafety: Math.floor(marginOfSafety),
      marginOfSafetyPercent,
      totalRevenue,
      totalCosts
    };
  };

  const generateCostRevenueData = () => {
    const fixed = parseFloat(fixedCosts) || 0;
    const varCost = parseFloat(variableCostPerUnit) || 0;
    const price = parseFloat(sellingPrice) || 0;
    const breakEven = calculateBreakEven().breakEvenUnits;
    
    const data = [];
    const maxUnits = Math.max(breakEven * 1.5, parseFloat(expectedSales) * 1.2);
    const step = Math.ceil(maxUnits / 10);

    for (let units = 0; units <= maxUnits; units += step) {
      const revenue = units * price;
      const totalCost = fixed + (units * varCost);
      const profit = revenue - totalCost;
      
      data.push({
        units: Math.round(units),
        revenue: Math.round(revenue),
        totalCost: Math.round(totalCost),
        profit: Math.round(profit)
      });
    }
    return data;
  };

  const generateScenarioData = () => {
    const fixed = parseFloat(fixedCosts) || 0;
    const varCost = parseFloat(variableCostPerUnit) || 0;
    const price = parseFloat(sellingPrice) || 0;
    const breakEven = calculateBreakEven().breakEvenUnits;

    const scenarios = [
      { name: '50% Below BEP', units: breakEven * 0.5 },
      { name: '75% of BEP', units: breakEven * 0.75 },
      { name: 'Break-Even', units: breakEven },
      { name: '125% of BEP', units: breakEven * 1.25 },
      { name: '150% of BEP', units: breakEven * 1.5 }
    ];

    return scenarios.map(scenario => {
      const revenue = scenario.units * price;
      const totalCost = fixed + (scenario.units * varCost);
      const profit = revenue - totalCost;
      
      return {
        name: scenario.name,
        units: Math.round(scenario.units),
        profit: Math.round(profit)
      };
    });
  };

  const generatePriceImpactData = () => {
    const fixed = parseFloat(fixedCosts) || 0;
    const varCost = parseFloat(variableCostPerUnit) || 0;
    const price = parseFloat(sellingPrice) || 0;

    const priceChanges = [-20, -10, 0, 10, 20];
    
    return priceChanges.map(change => {
      const newPrice = price * (1 + change / 100);
      const contributionMargin = newPrice - varCost;
      const breakEvenUnits = contributionMargin > 0 ? fixed / contributionMargin : 0;
      
      return {
        priceChange: `${change > 0 ? '+' : ''}${change}%`,
        price: newPrice.toFixed(2),
        breakEvenUnits: Math.ceil(breakEvenUnits)
      };
    });
  };

  const results = calculateBreakEven();
  const costRevenueData = generateCostRevenueData();
  const scenarioData = generateScenarioData();
  const priceImpactData = generatePriceImpactData();

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))'
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Break-Even Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is Break-Even Analysis?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Break-even analysis determines the point where your total revenues equal total costs, meaning you're neither 
                  making a profit nor a loss. This critical metric helps you understand how many units you need to sell to cover 
                  your costs and start generating profit.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Parameters</CardTitle>
                  <CardDescription>Enter your business costs and pricing</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="fixedCosts" className="text-sm font-medium">
                      Fixed Costs ($)
                    </Label>
                    <Input
                      id="fixedCosts"
                      type="number"
                      value={fixedCosts}
                      onChange={(e) => setFixedCosts(e.target.value)}
                      placeholder="50000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Rent, salaries, insurance, etc.</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="variableCostPerUnit" className="text-sm font-medium">
                      Variable Cost per Unit ($)
                    </Label>
                    <Input
                      id="variableCostPerUnit"
                      type="number"
                      value={variableCostPerUnit}
                      onChange={(e) => setVariableCostPerUnit(e.target.value)}
                      placeholder="20"
                      step="0.01"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Materials, labor, shipping per unit</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sellingPrice" className="text-sm font-medium">
                      Selling Price per Unit ($)
                    </Label>
                    <Input
                      id="sellingPrice"
                      type="number"
                      value={sellingPrice}
                      onChange={(e) => setSellingPrice(e.target.value)}
                      placeholder="50"
                      step="0.01"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="expectedSales" className="text-sm font-medium">
                      Expected Sales Volume (Units)
                    </Label>
                    <Input
                      id="expectedSales"
                      type="number"
                      value={expectedSales}
                      onChange={(e) => setExpectedSales(e.target.value)}
                      placeholder="2000"
                      className="text-base"
                    />
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setFixedCosts(fixedCosts)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Break-Even Analysis Results</CardTitle>
                    <CardDescription>Your profitability metrics</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Break-Even Point (Units)</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {results.breakEvenUnits.toLocaleString()}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">units to break even</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Break-Even Revenue</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(results.breakEvenRevenue)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Contribution Margin</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {formatCurrency(results.contributionMargin)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">{results.contributionMarginRatio.toFixed(1)}% margin ratio</p>
                      </div>

                      <div className={`p-6 rounded-xl bg-gradient-to-br ${results.profit >= 0 ? 'from-secondary-light to-card border-secondary/20' : 'from-destructive/10 to-card border-destructive/20'} border`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-10 h-10 rounded-lg ${results.profit >= 0 ? 'bg-secondary' : 'bg-destructive'} flex items-center justify-center`}>
                            {results.profit >= 0 ? <TrendingUp className="w-5 h-5 text-white" /> : <AlertTriangle className="w-5 h-5 text-white" />}
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Profit/Loss at {expectedSales} Units</p>
                        </div>
                        <p className={`text-3xl font-display font-bold ${results.profit >= 0 ? 'text-secondary' : 'text-destructive'}`}>
                          {formatCurrency(results.profit)}
                        </p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Margin of Safety</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {results.marginOfSafety.toLocaleString()} units
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {results.marginOfSafetyPercent.toFixed(1)}% buffer above break-even
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Total Costs</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {formatCurrency(results.totalCosts)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            at {expectedSales} units
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Cost vs Revenue Analysis</CardTitle>
                <CardDescription className="text-base">
                  Visualize your break-even point and profit zones
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={costRevenueData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="units" 
                        label={{ value: 'Units Sold', position: 'insideBottom', offset: -5 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft' }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="hsl(var(--secondary))" 
                        strokeWidth={3}
                        name="Total Revenue"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="totalCost" 
                        stroke="hsl(var(--destructive))" 
                        strokeWidth={3}
                        name="Total Cost"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Profit at Different Sales Levels</CardTitle>
                  <CardDescription>
                    Scenario analysis around break-even point
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={scenarioData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="name" 
                          stroke="hsl(var(--muted-foreground))"
                          angle={-15}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="profit" name="Profit/Loss">
                          {scenarioData.map((entry, index) => (
                            <Bar 
                              key={`bar-${index}`} 
                              fill={entry.profit >= 0 ? 'hsl(var(--secondary))' : 'hsl(var(--destructive))'}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Price Change Impact</CardTitle>
                  <CardDescription>
                    How pricing affects break-even point
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={priceImpactData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="priceChange" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          label={{ value: 'Break-Even Units', angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="breakEvenUnits" fill="hsl(var(--primary))" name="Break-Even Units" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Your Break-Even Analysis</CardTitle>
                <CardDescription className="text-base">
                  Learn how to interpret and use these metrics for better business decisions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    What is the Break-Even Point?
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your break-even point is <strong className="text-primary">{results.breakEvenUnits.toLocaleString()} units</strong> or <strong className="text-primary">{formatCurrency(results.breakEvenRevenue)}</strong> in revenue. 
                    This is the minimum sales volume needed to cover all your costs without making a loss.
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm">
                    <p className="font-mono mb-2">Break-Even Point = Fixed Costs ÷ Contribution Margin per Unit</p>
                    <p className="font-mono">
                      Break-Even Point = {formatCurrency(parseFloat(fixedCosts))} ÷ {formatCurrency(results.contributionMargin)} = {results.breakEvenUnits.toLocaleString()} units
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Contribution Margin Explained
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your contribution margin is <strong className="text-secondary">{formatCurrency(results.contributionMargin)}</strong> per unit ({results.contributionMarginRatio.toFixed(1)}%). 
                    This represents how much each sale contributes toward covering fixed costs and generating profit.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-secondary-light border border-secondary/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Per Unit</p>
                      <p className="text-2xl font-bold text-secondary mb-2">
                        {formatCurrency(results.contributionMargin)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Selling Price ({formatCurrency(parseFloat(sellingPrice))}) - Variable Cost ({formatCurrency(parseFloat(variableCostPerUnit))})
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border border-secondary/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Margin Ratio</p>
                      <p className="text-2xl font-bold text-secondary mb-2">
                        {results.contributionMarginRatio.toFixed(1)}%
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Of each dollar in sales
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Margin of Safety Analysis
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Your margin of safety is <strong className="text-accent">{results.marginOfSafety.toLocaleString()} units</strong> ({results.marginOfSafetyPercent.toFixed(1)}%). 
                    This shows how much sales can drop before you reach the break-even point and start losing money.
                  </p>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium">Risk Level:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        results.marginOfSafetyPercent > 30 ? 'bg-secondary/20 text-secondary' :
                        results.marginOfSafetyPercent > 15 ? 'bg-accent/20 text-accent' :
                        'bg-destructive/20 text-destructive'
                      }`}>
                        {results.marginOfSafetyPercent > 30 ? 'Low Risk' :
                         results.marginOfSafetyPercent > 15 ? 'Moderate Risk' :
                         'High Risk'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {results.marginOfSafetyPercent > 30 ? 
                        'Excellent! You have a healthy buffer. Sales can decline significantly before affecting profitability.' :
                       results.marginOfSafetyPercent > 15 ?
                        'Moderate safety margin. Consider increasing sales or reducing costs to improve your buffer.' :
                        'Low margin of safety. Focus on increasing sales volume or improving your contribution margin.'}
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Important Considerations and Risks
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Fixed vs Variable Classification:</strong> Ensure costs are 
                        properly categorized. Some costs may be semi-variable (partly fixed, partly variable).
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Assumes Linear Relationships:</strong> This analysis assumes 
                        costs and revenues scale linearly, which may not hold at very high or low volumes.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Market Conditions:</strong> Break-even analysis doesn't 
                        account for market demand limits, competition, or pricing power.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Multi-Product Complexity:</strong> If you sell multiple 
                        products, calculate break-even for each or use weighted averages.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Strategies to Improve Break-Even Point
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Increase Selling Price</h4>
                      <p className="text-muted-foreground">
                        Even small price increases can significantly reduce break-even units, but ensure market will accept higher prices.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Reduce Variable Costs</h4>
                      <p className="text-muted-foreground">
                        Negotiate better supplier rates, improve production efficiency, or reduce material waste.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ Lower Fixed Costs</h4>
                      <p className="text-muted-foreground">
                        Renegotiate rent, outsource non-core functions, or share resources to reduce overhead.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple">
                      <h4 className="font-semibold text-foreground mb-2">✅ Increase Sales Volume</h4>
                      <p className="text-muted-foreground">
                        Expand marketing efforts, enter new markets, or improve sales conversion rates.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default BreakEvenCalculator;
